import { useCallback, useEffect, useRef, useState } from 'react';
import { sortTracks } from '../sort-tracks';
import { TrackPreview } from '../timeline/types';

// How long a settled-but-unconverged overlay lingers before the backstop
// clears it: under Zero convergence normally lands in under a second, so
// the backstop only guards a base list that never converges.
const REORDER_SETTLE_TIMEOUT_MS = 5000;

/**
 * One sibling group's optimistic order: member ids in post-drag order.
 * `parentTrackId` is null for the parent level. `seq` guards settles — a
 * late settle of one drag cannot clear a later drag's overlay. `settled`
 * marks a success still waiting for the base list to converge.
 */
export type PendingReorderGroup = {
  parentTrackId: string | null;
  orderedIds: string[];
  seq: number;
  settled: boolean;
};

export type PendingReorder = {
  groups: PendingReorderGroup[];
};

function groupKey(parentTrackId: string | null): string {
  return parentTrackId ?? '';
}

function siblingGroupIds(tracks: TrackPreview[]): Map<string, string[]> {
  const byGroup = new Map<string, string[]>();
  for (const track of tracks) {
    const key = groupKey(track.parentTrackId ?? null);
    const ids = byGroup.get(key);
    if (ids) ids.push(track.id);
    else byGroup.set(key, [track.id]);
  }
  return byGroup;
}

function sameOrder(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

/**
 * The overlay a drop produces: every sibling group whose relative order
 * changed, as ordered-id lists. `rendered` is the list the drag happened on
 * (carrying any earlier overlay); `reordered` is the drop's result.
 *
 * Only changed groups are captured, so a remote reorder of an untouched
 * group is never masked.
 */
export function computePendingReorderGroups(
  rendered: TrackPreview[],
  reordered: TrackPreview[],
  seq: number,
): PendingReorderGroup[] {
  const renderedGroups = siblingGroupIds(rendered);
  const groups: PendingReorderGroup[] = [];
  for (const [key, reorderedIds] of siblingGroupIds(reordered)) {
    const renderedIds = renderedGroups.get(key) ?? [];
    if (sameOrder(renderedIds, reorderedIds)) continue;
    groups.push({
      parentTrackId: key === '' ? null : key,
      orderedIds: reorderedIds,
      seq,
      settled: false,
    });
  }
  return groups;
}

/**
 * Applies a pending reorder to the base list. Pure — a mutated sortOrder
 * would leak into the base list and break the snap-back when the overlay
 * clears.
 *
 * Each touched group is re-ordered by the overlay's ids and renumbered
 * 1..n (a drop's own numbering), so the list renders optimistically.
 * Tracks synced in after the drop are appended at the end of their group —
 * application is by id, so renames flow through.
 */
export function applyPendingReorder(
  tracks: TrackPreview[],
  pending: PendingReorder | null,
): TrackPreview[] {
  if (!pending || pending.groups.length === 0) return tracks;

  let next = tracks;
  for (const group of pending.groups) {
    const members = next.filter(
      (t) => (t.parentTrackId ?? null) === group.parentTrackId,
    );
    if (members.length === 0) continue;

    const memberById = new Map(members.map((t) => [t.id, t]));
    const orderedIds = new Set(group.orderedIds);
    const reorderedGroup = [
      ...group.orderedIds.flatMap((id) => {
        const track = memberById.get(id);
        return track ? [track] : [];
      }),
      // Tracks synced in mid-flight ride after the overlayed order, not dropped.
      ...members.filter((t) => !orderedIds.has(t.id)),
    ].map((t, index) => ({ ...t, sortOrder: index + 1 }));

    const replaced = new Map(reorderedGroup.map((t) => [t.id, t]));
    next = next.map((t) => replaced.get(t.id) ?? t);
  }
  // Renumbered sortOrders only take effect once the list is re-sorted by
  // them — the same re-sort the pendingSubTrackMove branch ends with.
  return sortTracks(next);
}

/**
 * Whether the base list has caught up with an overlayed group: the group's
 * ids appear in the base in the same relative order — relative, not raw
 * sortOrder, which the server renormalizes. Ids deleted or synced in
 * mid-flight are ignored on both sides.
 */
export function isGroupConverged(
  base: TrackPreview[],
  group: PendingReorderGroup,
): boolean {
  const baseIds = base
    .filter((t) => (t.parentTrackId ?? null) === group.parentTrackId)
    .map((t) => t.id);
  const overlaySet = new Set(group.orderedIds);
  const wanted = group.orderedIds.filter((id) => baseIds.includes(id));
  const actual = baseIds.filter((id) => overlaySet.has(id));
  return sameOrder(wanted, actual);
}

/**
 * Owns the drag-n-drop reorder overlay next to `tracksForRender`.
 *
 * A drop calls `beginReorder` with the rendered list and the drop's result;
 * the returned seq identifies this drag's settle. Failure clears immediately
 * — under Zero a refetch is a no-op, so "stop overlaying" is the rollback.
 * Success clears only once the base list matches the overlay's relative
 * order (few-second backstop): clearing outright would flash the pre-drag
 * order before the poke lands.
 */
export function usePendingReorder(tracks: TrackPreview[]) {
  const [pendingReorder, setPendingReorder] = useState<PendingReorder | null>(
    null,
  );
  const seqRef = useRef(0);
  const backstopsRef = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const clearGroups = useCallback((seq: number) => {
    const backstop = backstopsRef.current.get(seq);
    if (backstop) {
      clearTimeout(backstop);
      backstopsRef.current.delete(seq);
    }
    setPendingReorder((prev) => {
      if (!prev) return prev;
      const groups = prev.groups.filter((g) => g.seq !== seq);
      if (groups.length === prev.groups.length) return prev;
      return groups.length > 0 ? { groups } : null;
    });
  }, []);

  const beginReorder = useCallback(
    (rendered: TrackPreview[], reordered: TrackPreview[]): number => {
      const seq = ++seqRef.current;
      const groups = computePendingReorderGroups(rendered, reordered, seq);
      if (groups.length > 0) {
        setPendingReorder((prev) => {
          // Groups this drag also touched replace their earlier version (the
          // reordered list was built on top of it); untouched groups carry.
          const carried = (prev?.groups ?? []).filter(
            (g) => !groups.some((n) => n.parentTrackId === g.parentTrackId),
          );
          return { groups: [...carried, ...groups] };
        });
      }
      return seq;
    },
    [],
  );

  const settleReorder = useCallback(
    (seq: number, failed: boolean) => {
      if (failed) {
        clearGroups(seq);
        return;
      }
      // Mark this drag's groups settled; the convergence effect below clears
      // them as the base list catches up. A seq whose groups a later drag
      // already replaced matches nothing here — a stale settle is a no-op.
      setPendingReorder((prev) =>
        prev
          ? {
              groups: prev.groups.map((g) =>
                g.seq === seq ? { ...g, settled: true } : g,
              ),
            }
          : prev,
      );
      const backstop = setTimeout(
        () => clearGroups(seq),
        REORDER_SETTLE_TIMEOUT_MS,
      );
      backstopsRef.current.set(seq, backstop);
    },
    [clearGroups],
  );

  // Clear settled groups the base list has converged with. Runs per base-list
  // change so the poke/refetch that lands the new order also lands the clear.
  useEffect(() => {
    if (!pendingReorder) return;
    const remaining = pendingReorder.groups.filter(
      (g) => !(g.settled && isGroupConverged(tracks, g)),
    );
    if (remaining.length === pendingReorder.groups.length) return;
    setPendingReorder(remaining.length > 0 ? { groups: remaining } : null);
  }, [tracks, pendingReorder]);

  // Never fire a backstop after unmount.
  useEffect(() => {
    const backstops = backstopsRef.current;
    return () => {
      for (const backstop of backstops.values()) clearTimeout(backstop);
      backstops.clear();
    };
  }, []);

  return { pendingReorder, beginReorder, settleReorder };
}
